import * as React from 'react';
import { ListOrdered, Plus, Search, Settings } from 'lucide-react';
import {
  ListCount,
  ListGroupLabel,
  ListItem,
  ListPane,
  SearchField,
  SegmentedControl,
} from '@/components/ui/workspace';
import { useKbReview, type KbTreePartner, type KbTreeVendor } from '@/lib/api/vendor-knowledge';
import { Count } from './kb-bits';
import { PLATFORM_TYPES, matches, plural } from './kb-logic';
import { useKb } from './vendor-knowledge-screen';

/**
 * The left list (prototype `listPane()`): a search, All or Needs review
 * (curators), vendors grouped by platform type with their product count and
 * what waits for review, implementation partners, then the vendor lists
 * (everyone; T-0267), Add vendor and the settings at the foot (curators).
 * The review counts are the server's (`GET /review`, `by_vendor`).
 */
export function KbListPane({ className }: { className?: string }) {
  const { tree, canCurate, view, navigate } = useKb();
  const [query, setQuery] = React.useState('');
  const [filter, setFilter] = React.useState<'all' | 'review'>('all');
  const review = useKbReview(null, canCurate);
  const reviewOf = React.useMemo(
    () => new Map((review.data?.by_vendor ?? []).map((v) => [v.vendor_id, v.count])),
    [review.data],
  );

  const vendors = tree.vendors.filter((v) => v.status === 'active');
  const partners = tree.partners.filter((p) => p.status === 'active');
  const productName = new Map(
    tree.vendors.flatMap((v) => v.products.map((p) => [p.id, p.name] as const)),
  );
  const partnerNamesOf = (v: KbTreeVendor) =>
    partners
      .filter((p) => p.product_ids.some((id) => v.products.some((pr) => pr.id === id)))
      .map((p) => p.name);

  const keepVendor = (v: KbTreeVendor) =>
    matches(query, v.name, ...v.products.map((p) => p.name), ...partnerNamesOf(v)) &&
    (filter === 'all' || (reviewOf.get(v.id) ?? 0) > 0);
  const keepPartner = (p: KbTreePartner) =>
    filter === 'all' && matches(query, p.name, ...p.product_ids.map((id) => productName.get(id)));

  const groups = PLATFORM_TYPES.map((t) => ({
    name: t.label,
    vendors: vendors.filter((v) => (v.platform_type ?? 'other') === t.id && keepVendor(v)),
  })).filter((g) => g.vendors.length > 0);
  const shownPartners = partners.filter(keepPartner);
  const needsReview = vendors.filter((v) => (reviewOf.get(v.id) ?? 0) > 0).length;

  const vendorRow = (v: KbTreeVendor) => {
    const n = canCurate ? (reviewOf.get(v.id) ?? 0) : 0;
    const known = v.products.some((p) => p.confirmed_items > 0);
    const selected = view.kind === 'vendor' && view.id === v.id;
    return (
      <ListItem
        key={v.id}
        selected={selected}
        title={v.name}
        meta={plural(v.products.filter((p) => p.status === 'active').length, 'product')}
        trailing={
          n > 0 ? (
            <ListCount title={`${plural(n, 'thing')} to review`}>{n}</ListCount>
          ) : !known ? (
            <span className="flex-none whitespace-nowrap text-[11px] leading-4 text-ink-300">
              No knowledge yet
            </span>
          ) : null
        }
        onClick={() => navigate({ kind: 'vendor', id: v.id })}
      />
    );
  };

  const group = (name: string, children: React.ReactNode) => (
    <div key={name} role="group" aria-label={name}>
      <ListGroupLabel>{name}</ListGroupLabel>
      {children}
    </div>
  );

  return (
    <ListPane
      label="Vendors and partners"
      className={className}
      head={
        <>
          <SearchField
            value={query}
            onChange={setQuery}
            aria-label="Search vendors, products, partners"
            placeholder="Search vendors, products, partners"
            icon={<Search className="h-3.5 w-3.5" aria-hidden="true" />}
            wrapperClassName="w-full gap-1.5 px-2"
          />
          {canCurate ? (
            <SegmentedControl
              aria-label="Show"
              value={filter}
              onChange={setFilter}
              className="flex w-full [&>button]:flex-1"
              options={[
                {
                  value: 'all',
                  label: (
                    <>
                      All{' '}
                      <Count className="ml-0.5 text-[11px] text-current opacity-75">
                        {vendors.length + partners.length}
                      </Count>
                    </>
                  ),
                },
                {
                  value: 'review',
                  label: (
                    <>
                      Needs review{' '}
                      <Count className="ml-0.5 text-[11px] text-current opacity-75">
                        {needsReview}
                      </Count>
                    </>
                  ),
                },
              ]}
            />
          ) : null}
        </>
      }
      foot={
        <>
          <ListItem
            selected={view.kind === 'lists' || view.kind === 'list'}
            leading={
              <ListOrdered className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
            }
            title="Vendor lists"
            onClick={() => navigate({ kind: 'lists' })}
          />
          {canCurate ? (
            <>
              <ListItem
                selected={view.kind === 'new'}
                leading={<Plus className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />}
                title="Add vendor"
                onClick={() => navigate({ kind: 'new' })}
              />
              <ListItem
                selected={view.kind === 'settings'}
                leading={
                  <Settings className="h-3.5 w-3.5 flex-none text-ink-500" aria-hidden="true" />
                }
                title="Knowledge base settings"
                onClick={() => navigate({ kind: 'settings' })}
              />
            </>
          ) : null}
        </>
      }
    >
      {groups.map((g) => group(g.name, g.vendors.map(vendorRow)))}
      {shownPartners.length > 0
        ? group(
            'Implementation partners',
            shownPartners.map((p) => (
              <ListItem
                key={p.id}
                selected={view.kind === 'partner' && view.id === p.id}
                title={p.name}
                meta="Partner"
                onClick={() => navigate({ kind: 'partner', id: p.id })}
              />
            )),
          )
        : null}
      {groups.length === 0 && shownPartners.length === 0 ? (
        <div className="px-2 py-3 text-meta text-ink-500">
          {filter === 'review' && !query.trim()
            ? 'Nothing needs review.'
            : vendors.length + partners.length === 0
              ? 'No vendors yet.'
              : 'No vendor, product or partner matches.'}
        </div>
      ) : null}
    </ListPane>
  );
}
